import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  Spinner,
  Card,
  CardBody,
} from "reactstrap";
import { OPERATE_MQTT } from "../../../../helpers/socketEvent";
import * as url from "../../../../helpers/url_helper";
import { toast } from "react-toastify";
import { useSocket } from "../../../../Utils/socket";
import Request from "../../../../helpers/request";
import { convertLocalDate } from "../../../../Utils/configController";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import AirHomeLoader from "../../../../components/Common/AirHomeLoader";
import DataTable from "react-data-table-component";
import {
  FormatJsonCheckBox,
  RefreshBtn,
} from "../../../../components/Common/button";

export interface modelType {
  headerTitle: string;
  otaSlave?: any;
  isHideLog?: boolean;
  cancleBtnClick(): void;
}

// Compares two version strings (e.g., "1.9.4" and "1.9.5")
// Returns:
//   -1 if a is greater than b
//    1 if a is less than b
//    0 if both are equal
const compareVersions = (a: string, b: string) => {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  const len = Math.max(pa.length, pb.length);

  for (let i = 0; i < len; i++) {
    const na = pa[i] ?? 0;
    const nb = pb[i] ?? 0;
    if (na > nb) return -1;
    if (na < nb) return 1;
  }
  return 0;
};

// Increments the last segment of the version string by 1
// Example:
//   "1.9.4" => "1.9.5"
function incrementLastSegment(version: string) {
  const parts = version.split(".").map(Number);

  for (let i = parts.length - 1; i >= 0; i--) {
    parts[i] += 1;

    if (parts[i] < 10) {
      break;
    } else {
      parts[i] = 0;
      if (i === 0) {
        parts.unshift(1);
      }
    }
  }

  return parts.join(".");
}

// Replaces "https://" with "http://" in the URL string
// If the URL is null or undefined, returns null
// Example:
//   "https:" => "http:"
function replaceUrl(url: string) {
  if (!url) {
    return null;
  }
  return url.replace("https://", "http://");
}

const OtaModel = ({
  headerTitle,
  otaSlave,
  isHideLog = false,
  cancleBtnClick,
}: modelType) => {
  const socket: any = useSocket();
  const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const [firmwareData, setFirmwareData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [debugLogData, setDebugLogData] = useState<any[]>([]);
  const [debugLoading, setDebugLoading] = useState<boolean>(true);
  const [showFormatted, setShowFormatted] = useState<boolean>(false);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<{
    slave: string;
    binFilePath: string | null;
    version: string;
    product?: string;
    token?: string;
  } | null>(null);
  const [useQaEnv, setUseQaEnv] = useState(false);

  const fetchFirmwareList = () => {
    setLoading(true);
    const reqObj: Record<string, unknown> = {
      start: 0,
      length: 100,
      search: {
        value: otaSlave?.hwVersion || "",
        product: otaSlave?.product || "",
        regex: false,
      },
    };
    if (useQaEnv) {
      reqObj.env = "qa";
    }

    Request.post(url.GET_FIRMWARE_LIST, reqObj)
      .then((res) => {
        const sortedList = res?.data?.data?.sort((a: any, b: any) => {
          return b.isStable - a.isStable;
        });
        setFirmwareData(sortedList);
        setLoading(false);
      })
      .catch((err) => {
        console.error("err: ", err);
      });
  };
  const fetchLogList = () => {
    setDebugLoading(true);
    const reqObj = {
      draw: 1,
      start: 0,
      length: 5,
      search: {
        value: "",
        regex: false,
      },
      slave: otaSlave?.slave,
      filterType: 0,
    };

    Request.post(url.DEBUG_DETAIL_LIST, reqObj)
      .then((res) => {
        setDebugLogData(res?.data?.data);
        setDebugLoading(false);
      })
      .catch((err) => {
        console.error("err: ", err);
      });
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (otaSlave !== undefined) {
      fetchFirmwareList();
      if (!isHideLog) {
        fetchLogList();
        intervalId = setInterval(() => {
          fetchLogList();
        }, 30000);
      }
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [otaSlave, isHideLog, useQaEnv]);

  const handelClickOta = (
    slave: string,
    binFilePath: string | null,
    version: string,
    product?: string,
    token?: string,
  ) => {
    if (!slave) return;

    let reqObj;

    if (product === "airblaster") {
      reqObj = {
        cmd: "BOTA",
        device_ID: slave,
        data: [version, binFilePath],
        token: token,
      };
    } else {
      reqObj = {
        slave: slave,
        tag: `airpanel-${userInfo?.id}`,
        data: [version, binFilePath],
        cmd: "OT",
      };
    }

    socket.emit(OPERATE_MQTT, JSON.stringify(reqObj));
    toast.success(`OTA sent to slave ${slave}`);
    setIsConfirmModalOpen(null);
    setTimeout(() => {
      fetchLogList();
    }, 2000);
  };

  const latestStable = useMemo(() => {
    if (!firmwareData || !Array.isArray(firmwareData)) return null;
    return (
      firmwareData
        .filter((f) => f.isStable)
        .sort((a, b) =>
          compareVersions(a.firmwareVersion, b.firmwareVersion),
        )[0] ?? null
    );
  }, [firmwareData]);
  const options = useMemo(() => {
    const step = 5;
    const max = firmwareData?.length || 0;
    const result = [];

    for (let i = step; i < max; i += step) {
      result.push(i);
    }

    if (max > 0) {
      result.push(max); // Add 'All'
    }

    return result;
  }, [firmwareData]);

  const handleMQTTData = () => {
    otaSlave?.slave
      ? window.open(`/debug-device-details/${otaSlave?.slave}`, "_blank")
      : "";
  };

  const handleReload = () => {
    fetchLogList();
  };

  const columns = [
    {
      name: "#",
      cell: (_row: any, index: number) => index + 1, // ✅ use `cell` instead of `selector`
      width: "60px",
    },
    {
      name: "Command",
      cell: (row: any) => {
        try {
          const parsed = JSON.parse(row?.command);
          return <div>{parsed.cmd ? parsed.cmd : "-"}</div>;
        } catch (e) {
          return "-";
        }
      },
      wrap: true,
      grow: 0.5,
    },
    {
      name: "TX",
      cell: (row: any) => {
        const isDevice =
          row.topic.includes("/TX") || row.topic.includes("/g2m");
        const content = isDevice
          ? showFormatted
            ? formatCommand(row.command)
            : row.command
          : "-";
        return (
          <div
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              padding: "10px 2px",
            }}
          >
            {content}
          </div>
        );
      },
      wrap: true,
      grow: 2,
    },
    {
      name: "RX",
      cell: (row: any) => {
        const isMobile =
          row.topic.includes("/RX") || row.topic.includes("/m2g");
        const content = isMobile
          ? showFormatted
            ? formatCommand(row.command)
            : row.command
          : "-";
        return (
          <div
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              padding: "10px 2px",
            }}
          >
            {content}
          </div>
        );
      },
      wrap: true,
      grow: 2,
    },
    {
      name: "Created At",
      selector: (row: any) => convertLocalDate(row.createdAt),
      // width: "160px",
    },
  ];

  const formatCommand = (command: string) => {
    try {
      return JSON.stringify(JSON.parse(command), null, 2);
    } catch {
      return command;
    }
  };

  return (
    <div>
      {isConfirmModalOpen && (
        <ConfirmationModal
          headerTitle="Confirm OTA?"
          text="Are you sure you want to update the firmware on this device? This action will apply the selected OTA version and cannot be undone."
          buttonText="OTA"
          onClick={() => {
            handelClickOta(
              isConfirmModalOpen?.slave,
              isConfirmModalOpen.binFilePath,
              isConfirmModalOpen.version,
              isConfirmModalOpen?.product,
              isConfirmModalOpen?.token,
            );
          }}
          cancleBtnClick={() => setIsConfirmModalOpen(null)}
          loading={loading}
        />
      )}
      <Modal isOpen={true} className="modal-xl">
        <div className="modal-header justify-content-between align-items-center flex-wrap gap-2">
          <h5
            className="modal-title mb-0 flex-grow-1 min-w-0 text-truncate"
            id="staticBackdropLabel"
            title={headerTitle}
          >
            {headerTitle}
          </h5>
          <div className="d-flex align-items-center gap-3 gap-sm-4 flex-shrink-0 ms-auto">
            {otaSlave?.slave && (
              <>
                <h6
                  className="modal-title mb-0"
                  style={{ textAlign: "center", fontSize: "1.1rem" }}
                >
                  Slave: {otaSlave?.slave}
                </h6>
                <div
                  className="vr align-self-stretch opacity-25 flex-shrink-0 d-none d-sm-block"
                  aria-hidden
                />
              </>
            )}
            <div className="fs-4 form-check p-0 form-switch m-0 d-flex align-items-center flex-shrink-0">
              <input
                className="form-check-input ms-0 me-2"
                type="checkbox"
                role="switch"
                id="ota-modal-qa-env"
                checked={useQaEnv}
                onChange={(e) => setUseQaEnv(e.target.checked)}
              />
              <label
                className="form-check-label text-secondary small mb-0 user-select-none pt-1"
                htmlFor="ota-modal-qa-env"
              >
                QA
              </label>
            </div>
          </div>
        </div>

        <ModalBody style={{ padding: 0 }}>
          <div
            style={{
              maxHeight: "70vh",
              overflowY: "auto",
              overflowX: "auto",
              backgroundColor: "#F7F7FA",
              padding: "20px",
            }}
          >
            <Card>
              <CardBody>
                <div className="" style={{ width: "100%", overflowX: "auto" }}>
                  <table className="table table-hover table-sm ">
                    {/* className="sticky-top" */}
                    <thead>
                      <tr>
                        <th
                          style={{
                            minWidth: "50px",
                            backgroundColor: "#EFF2F7",
                          }}
                        >
                          <select
                            value={rowsPerPage}
                            onChange={(e) =>
                              setRowsPerPage(Number(e.target.value))
                            }
                            className="form-select form-select-sm"
                            style={{ width: "100px" }}
                          >
                            {options.map((value) => (
                              <option key={value} value={value}>
                                {value === firmwareData.length
                                  ? `All (${value})`
                                  : value}
                              </option>
                            ))}
                          </select>
                        </th>
                        <th
                          style={{
                            minWidth: "150px",
                            backgroundColor: "#EFF2F7",
                          }}
                        >
                          Name
                        </th>
                        <th
                          style={{
                            minWidth: "150px",
                            backgroundColor: "#EFF2F7",
                          }}
                        >
                          Hardware Version
                        </th>
                        <th
                          style={{
                            minWidth: "150px",
                            backgroundColor: "#EFF2F7",
                          }}
                        >
                          Firmware Version
                        </th>
                        <th
                          style={{
                            minWidth: "200px",
                            backgroundColor: "#EFF2F7",
                          }}
                        >
                          Update
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={5}>
                            {" "}
                            <div
                              className="d-flex justify-content-center align-items-center"
                              style={{ height: "150px" }}
                            >
                              <Spinner color="dark" />
                            </div>
                          </td>
                        </tr>
                      ) : (
                        firmwareData
                          ?.slice(0, rowsPerPage)
                          ?.map((item: any, index: number) => {
                            const isYellow =
                              item.firmwareVersion === otaSlave?.wifiVersion;
                            const isGreen =
                              latestStable?.firmwareVersion ===
                              item.firmwareVersion;
                            const isStable = item.isStable;

                            const bgColor = isGreen
                              ? "#d1e7dd" // green
                              : isYellow
                              ? "#fff3cd" // yellow
                              : isStable
                              ? "#d1e7dd" // green
                              : "#f8d7da"; // red

                            const textColor = isYellow
                              ? "#664d03"
                              : isStable
                              ? "#0f5132"
                              : "#842029";

                            return (
                              <tr key={index}>
                                <td
                                  style={{
                                    backgroundColor: bgColor,
                                    color: textColor,
                                  }}
                                >
                                  {index + 1}
                                </td>
                                <td
                                  style={{
                                    backgroundColor: bgColor,
                                    color: textColor,
                                  }}
                                >
                                  {item.name}
                                </td>
                                <td
                                  style={{
                                    backgroundColor: bgColor,
                                    color: textColor,
                                  }}
                                >
                                  {item.hardwareVersion}
                                </td>
                                <td
                                  style={{
                                    backgroundColor: bgColor,
                                    color: textColor,
                                  }}
                                >
                                  {item.firmwareVersion}
                                </td>
                                <td
                                  style={{
                                    backgroundColor: bgColor,
                                    color: textColor,
                                  }}
                                >
                                  {isYellow ? (
                                    ""
                                  ) : (
                                    <button
                                      // onClick={() =>
                                      //   setIsConfirmModalOpen({
                                      //     slave: otaSlave.slave,
                                      //     binFilePath: replaceUrl(
                                      //       item?.binFilePath,
                                      //     ),
                                      //     version: incrementLastSegment(
                                      //       otaSlave?.wifiVersion,
                                      //     ),
                                      //   })
                                      // }
                                      onClick={() =>
                                        setIsConfirmModalOpen({
                                          slave: otaSlave.slave,
                                          binFilePath: replaceUrl(
                                            item?.binFilePath,
                                          ),
                                          version: incrementLastSegment(
                                            otaSlave?.wifiVersion,
                                          ),
                                          product: otaSlave?.product,
                                          token: otaSlave?.token,
                                        })
                                      }
                                      style={{
                                        border: "none",
                                        outline: "none",
                                        background: "none",
                                      }}
                                    >
                                      <span
                                        className="badge bg-primary"
                                        style={{
                                          height: "25px",
                                          width: "60px",
                                          fontSize: "12px",
                                          padding: "6px",
                                        }}
                                      >
                                        OTA
                                      </span>
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
            {!isHideLog ? (
              <Card>
                <CardBody>
                  <div className=" justify-content-between pb-2">
                    <div className="d-flex flex-row justify-content-between align-items-start">
                      <h5
                        className="modal-title"
                        id="staticBackdropLabel"
                        style={{ textAlign: "center" }}
                      >
                        {"Device Details"}
                      </h5>

                      <div className="d-flex align-items-center justify-content-center gap-3">
                        <FormatJsonCheckBox
                          checked={showFormatted}
                          onChange={() => setShowFormatted(!showFormatted)}
                        />

                        <RefreshBtn onClick={handleReload} loading={loading} />
                      </div>
                    </div>
                  </div>
                  {debugLoading && <AirHomeLoader />}
                  <DataTable
                    columns={columns}
                    data={debugLogData}
                    noDataComponent="No records found."
                    customStyles={customStyles}
                    highlightOnHover
                  />
                  <div
                    className="d-flex justify-content-center align-items-center mt-2"
                    style={{ width: "100%" }}
                  >
                    {otaSlave?.slave && (
                      <button
                        onClick={() => handleMQTTData()}
                        style={{
                          border: "none",
                          outline: "none",
                          background: "none",
                        }}
                      >
                        <span
                          className="badge bg-primary"
                          style={{
                            height: "25px",
                            fontSize: "12px",
                            padding: "6px",
                          }}
                        >
                          More Logs
                        </span>
                      </button>
                    )}
                  </div>
                </CardBody>
              </Card>
            ) : null}
          </div>
        </ModalBody>

        <ModalFooter
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", gap: "10px" }}>
            {[
              {
                bgColor: "#fff3cd",
                textColor: "#664d03",
                label: "Current Version",
              },
              {
                bgColor: "#d1e7dd",
                textColor: "#0f5132",
                label: "Stable Version",
              },
              {
                bgColor: "#f8d7da",
                textColor: "#842029",
                label: "Not Stable Version",
              },
            ].map((version, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: version.bgColor,
                  color: version.textColor,
                  borderRadius: "20px",
                  padding: "6px 12px",
                  display: "inline-block",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
              >
                <span>{version.label}</span>
              </div>
            ))}
          </div>

          <button
            className="btn btn-outline-secondary"
            onClick={cancleBtnClick}
            style={{ marginLeft: "auto" }}
          >
            Cancel
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default OtaModel;

export const customStyles: any = {
  headRow: {
    style: {
      backgroundColor: "#EFF2F7",
      fontWeight: "bold",
      fontSize: "14px",
      borderBottom: "0px solid #ced4da", // header bottom border
    },
  },
};
